/**
 * The portal's single typed model of a site's widget configuration
 * (contracts/widget-config.md, server/src/schemas/widget-config.ts).
 *
 * Every config screen edits ONE `WidgetConfigModel` held by the draft
 * provider; each section component owns exactly one top-level group. The
 * server stores and returns the parsed document for the groups on main:
 * branding, copy, placement, scope, safety, capture, routing, handoff,
 * analytics. Saving sends only the groups that changed, as whole group
 * objects (the server merges `{ ...stored[group], ...incoming[group] }`).
 *
 * Two keys come from parallel PRs whose shapes the orchestrator fixed
 * (2026-09-24) and are modelled here in full:
 *   - `placement.size`  — PR #864, claude/widget-size-config
 *   - `routing.actions` — PR #865, claude/widget-result-handoff (the
 *                         result-screen handoff list lives in `routing`)
 * A server built from main before those merge strips both keys on save
 * (its schema does not know them); `normalisedFields` treats "the server
 * returned nothing for this key" as "not stored by this server version"
 * rather than as a rejected value.
 *
 * `EXTENSION_GROUP_KEYS` is the escape hatch for the NEXT such key: a key
 * listed there round-trips untouched through the model, so a draft saved by
 * a newer portal is never clobbered by an older one.
 *
 * Defaults mirror `WIDGET_CONFIG_DEFAULTS` exactly (the no-published-row
 * document). `null` in branding/copy means "keep the bundle's own compiled
 * value", never "blank" — see the server file's header.
 */

export const CONFIG_GROUPS = [
  'branding',
  'copy',
  'placement',
  'scope',
  'safety',
  'capture',
  'routing',
  'handoff',
  'analytics',
] as const;
export type ConfigGroup = (typeof CONFIG_GROUPS)[number];

/**
 * Ten canonical trades (contracts/taxonomy.md) — the `scope.trades` vocabulary.
 * The labels are server/src/constants/trade-labels.ts's (the server puts the
 * same label on every lead as `trade_label`, and in lead emails);
 * model.test.ts reads that file and fails if the two drift.
 */
export const TRADES = [
  { slug: 'plumbing', label: 'Plumbing' },
  { slug: 'hvac', label: 'Heating & cooling' },
  { slug: 'electrical', label: 'Electrical' },
  { slug: 'appliance', label: 'Appliances' },
  { slug: 'water_damage_mold', label: 'Water damage & mold' },
  { slug: 'roofing_gutters', label: 'Roofing & gutters' },
  { slug: 'pest', label: 'Pest control' },
  { slug: 'handyman_general', label: 'Handyman' },
  { slug: 'exterior', label: 'Exterior repairs' },
  { slug: 'interior', label: 'Interior repairs' },
] as const;

/** A trade slug as a partner reads it; an unknown slug is humanised, never dropped. */
export function tradeLabel(slug: string | null | undefined): string {
  if (!slug) return 'Home repair';
  const known = TRADES.find((t) => t.slug === slug)?.label;
  if (known) return known;
  const words = slug.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Home repair';
}

/** server/src/schemas/widget-lead.ts `CONTACT_FIELDS`. */
export const CONTACT_FIELDS = [
  'first_name',
  'last_name',
  'phone_1',
  'email',
  'address_1',
  'address_2',
  'city',
  'state',
  'postal_code',
  'preferred_contact',
  'preferred_window',
] as const;
export type ContactField = (typeof CONTACT_FIELDS)[number];

export const ROUTING_MODES = ['finderly_app', 'partner_dispatch', 'partner_call', 'partner_booking', 'diy_only'] as const;
export type RoutingMode = (typeof ROUTING_MODES)[number];

export interface CustomQuestion {
  id: string;
  trade: string | null;
  label: string;
  type: 'text' | 'select' | 'boolean';
  options: string[];
  required: boolean;
}

export const ACTION_MODES = ['partner_call', 'partner_booking', 'partner_dispatch', 'finderly_app'] as const;
export type ActionMode = (typeof ACTION_MODES)[number];

/** One result-screen action (contracts/widget-config.md §Result actions, PR #865). */
export interface RoutingAction {
  id: string;
  mode: ActionMode;
  /** Button text, 1–60. */
  label: string;
  /** partner_call only: E.164, or null = roster lookup. */
  phone: string | null;
  /** partner_booking only: https link, or null = the contact form. */
  url: string | null;
  /** Empty = every trade. */
  trades: string[];
}
export const ROUTING_ACTIONS_MAX = 4;
export const ROUTING_ACTION_LABEL_MAX = 60;

/** The card's box (contracts/widget-config.md §Card size, PR #864). */
export interface PlacementSize {
  width: 'auto' | 'fill' | 'max';
  /** 280–720; required when width = max, ignored otherwise. */
  max_width_px: number | null;
  /** THE card height, 360–1200 (every card is fixed-height, 2026-10-02). */
  max_height_px: number | null;
  /** Kept for the wire shape; always written equal to max_height_px. */
  min_height_px: number | null;
  density: 'compact' | 'regular' | 'roomy';
}
export const SIZE_LIMITS = {
  max_width_px: { min: 280, max: 720 },
  max_height_px: { min: 360, max: 1200 },
  min_height_px: { min: 360, max: 1200 },
} as const;

/**
 * Every card is a fixed height (server WIDGET_CARD_HEIGHT_DEFAULT, owner
 * decision 2026-10-02): the partner's page never moves between steps. There
 * is no grow-with-content mode; a stored "grow" (both null) reads as this.
 */
export const CARD_HEIGHT_DEFAULT = 640;

export interface WidgetConfigModel {
  branding: {
    logo_url: string | null;
    /** `border`: the card outline and tab outline (decorative; null = derived from the card colours). */
    colors: { primary: string | null; on_primary: string | null; surface: string | null; text: string | null; border: string | null };
    radius_px: number | null;
    /** "inherit" = the host page's font, "system" = the widget's own stack, else a CSS stack. */
    font_family: string;
    theme: 'auto' | 'light' | 'dark';
    powered_by: boolean;
  };
  copy: {
    locale: string;
    headline: string | null;
    /** The line under the headline; null = the widget's own. */
    subheadline: string | null;
    cta_capture: string | null;
    cta_route: string | null;
    /** The one copy field where `null` means HIDE (the app handoff button). */
    cta_app: string | null;
    disclaimer_md: string | null;
    out_of_scope_message: string | null;
    sent_confirmation_matched: string | null;
    sent_confirmation_unmatched: string | null;
    audio_notice: string | null;
    /** Spec 013: the notice before a live conversation (spec 014: the "More" text). */
    live_notice: string | null;
    /** Spec 014: the one line always shown above Talk live / Chat. */
    live_notice_short: string | null;
    /** Spec 014: the AI notice for chat. */
    chat_notice: string | null;
    notice_at_collection_md: string | null;
  };
  placement: {
    mode: 'inline' | 'launcher';
    seed_context_allowed: boolean;
    identity_token_required: boolean;
    size: PlacementSize;
  };
  scope: {
    trades: string[];
    out_of_scope_behavior: 'diagnose_then_message' | 'refuse' | 'diagnose_then_finderly';
    service_area: {
      mode: 'none' | 'postal_codes';
      postal_codes: string[];
      out_of_area_behavior: 'capture_anyway' | 'refuse' | 'finderly_network';
    };
    /** `live` (spec 013) is a REQUEST, honoured only behind WIDGET_LIVE_ENABLED. */
    modes: { photo: boolean; audio: boolean; live: boolean; chat: boolean };
    /** The tab a visitor lands on (a mode that is on); null = the widget's choice. */
    default_mode: 'photo' | 'audio' | 'live' | 'chat' | null;
    /** Stored part only; `max_seconds` is server-owned and response-only. */
    live: { camera: boolean };
    custom_questions: CustomQuestion[];
  };
  safety: {
    emergency_bypass: boolean;
    emergency_message_md: string | null;
    emergency_phone: string | null;
    /** Fixed `false` by the contract; never editable. */
    emergency_dismissible: false;
    low_confidence_threshold: number;
    low_confidence_behavior: 'capture_anyway' | 'human_escalation' | 'message_only';
  };
  capture: {
    timing: 'after_diagnosis' | 'before_route';
    fields: { required: string[]; optional: string[] };
    prefill_from_identity_token: string[];
    consent: {
      mode: 'consumer_directed' | 'pewc';
      text_md: string | null;
      sms_opt_in: boolean;
      trustedform: boolean;
    };
  };
  routing: {
    mode: RoutingMode;
    diy_first: boolean;
    primary_cta: 'route' | 'diy';
    call: { phone_lookup: 'roster' | 'static' };
    urgency_labels: { emergency: string; urgent: string; routine: string };
    /** ≤ 4; `[]` = today's single CTA + app button. Non-empty replaces both. */
    actions: RoutingAction[];
  };
  handoff: {
    app_enabled: boolean;
    return_url: string | null;
  };
  analytics: {
    push_data_layer: boolean;
  };
}

/** Unmodelled keys per group, carried verbatim through load → save. */
export const EXTENSION_GROUP_KEYS: Record<string, readonly string[]> = {};

export const DEFAULT_CONFIG: WidgetConfigModel = Object.freeze({
  branding: {
    logo_url: null,
    colors: { primary: null, on_primary: null, surface: null, text: null, border: null },
    radius_px: null,
    font_family: 'inherit',
    theme: 'auto',
    powered_by: true,
  },
  copy: {
    locale: 'en-US',
    headline: null,
    subheadline: null,
    cta_capture: null,
    cta_route: null,
    cta_app: 'Continue in the app',
    disclaimer_md: null,
    out_of_scope_message: null,
    sent_confirmation_matched: null,
    sent_confirmation_unmatched: null,
    audio_notice: null,
    live_notice: null,
    live_notice_short: null,
    chat_notice: null,
    notice_at_collection_md: null,
  },
  placement: {
    mode: 'inline',
    seed_context_allowed: true,
    identity_token_required: false,
    size: { width: 'auto', max_width_px: null, max_height_px: CARD_HEIGHT_DEFAULT, min_height_px: CARD_HEIGHT_DEFAULT, density: 'regular' },
  },
  scope: {
    trades: [],
    out_of_scope_behavior: 'diagnose_then_finderly',
    service_area: { mode: 'none', postal_codes: [], out_of_area_behavior: 'capture_anyway' },
    // A new site's first config has the live conversation and chat on and the
    // camera off (server/src/schemas/widget-config.ts `newPartnerStoredConfig`).
    modes: { photo: true, audio: true, live: true, chat: true },
    default_mode: null,
    live: { camera: false },
    custom_questions: [],
  },
  safety: {
    emergency_bypass: true,
    emergency_message_md: null,
    emergency_phone: null,
    emergency_dismissible: false,
    low_confidence_threshold: 0.55,
    low_confidence_behavior: 'capture_anyway',
  },
  capture: {
    timing: 'after_diagnosis',
    fields: { required: ['first_name', 'phone_1'], optional: ['email', 'address_1', 'postal_code', 'preferred_window'] },
    prefill_from_identity_token: [],
    consent: { mode: 'consumer_directed', text_md: null, sms_opt_in: false, trustedform: false },
  },
  routing: {
    mode: 'finderly_app',
    diy_first: true,
    primary_cta: 'route',
    call: { phone_lookup: 'roster' },
    urgency_labels: { emergency: 'Emergency', urgent: 'Same day', routine: 'This week' },
    actions: [],
  },
  handoff: { app_enabled: true, return_url: null },
  analytics: { push_data_layer: true },
}) as WidgetConfigModel;

/** `GET /partner/sites/:key/config` — the stored document plus row metadata. */
export type StoredConfigResponse = Partial<WidgetConfigModel> & {
  schema_version: string;
  site_key: string;
  version: number;
  published: boolean;
  updated_by: number | null;
  updated_at: string | null;
} & Record<string, unknown>;

export interface ConfigMeta {
  version: number;
  published: boolean;
  updatedAt: string | null;
  /** `partner_users.id` of whoever saved this version (an agent saves as the person who created its token). */
  updatedBy: number | null;
}

export interface LoadedConfig {
  meta: ConfigMeta;
  config: WidgetConfigModel;
  /** Unmodelled keys (extension branches), carried through untouched. */
  extensions: Partial<Record<ConfigGroup, Record<string, unknown>>>;
}

export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Deep-merges a server document onto the defaults, keeping only keys the
 * defaults know. The server has already normalised it; this is the boundary
 * guard against a shape drift, not a second validator.
 */
/** Nullable fields whose non-null value is a number (every other nullable is a string). */
const NULLABLE_NUMBERS = new Set(['branding.radius_px', 'placement.size.max_width_px', 'placement.size.max_height_px', 'placement.size.min_height_px']);

function mergeKnown<T>(defaults: T, incoming: unknown, path = ''): T {
  if (!isRecord(defaults) || !isRecord(incoming)) {
    if (Array.isArray(defaults)) return (Array.isArray(incoming) ? clone(incoming) : clone(defaults)) as T;
    if (incoming === undefined) return clone(defaults);
    if (defaults === null) {
      const want = NULLABLE_NUMBERS.has(path) ? 'number' : 'string';
      return (incoming === null || typeof incoming === want ? incoming : null) as T;
    }
    if (incoming === null) return (typeof defaults === 'string' ? null : defaults) as T;
    return (typeof incoming === typeof defaults ? incoming : defaults) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, def] of Object.entries(defaults)) {
    out[key] = mergeKnown(def, incoming[key], path ? `${path}.${key}` : key);
  }
  return out as T;
}

export function parseConfig(raw: StoredConfigResponse | Record<string, unknown>): LoadedConfig {
  const record = raw as Record<string, unknown>;
  const config = {} as WidgetConfigModel;
  const extensions: LoadedConfig['extensions'] = {};
  for (const group of CONFIG_GROUPS) {
    (config as unknown as Record<string, unknown>)[group] = mergeKnown(DEFAULT_CONFIG[group], record[group], group);
    const incoming = record[group];
    const extKeys = EXTENSION_GROUP_KEYS[group] ?? [];
    if (isRecord(incoming)) {
      for (const key of extKeys) {
        if (key in incoming) {
          extensions[group] = { ...(extensions[group] ?? {}), [key]: clone(incoming[key]) };
        }
      }
    }
  }
  config.routing.actions = normaliseActions(config.routing.actions);
  // The widget caps an `auto` card at a set max_width_px (e2e agent run
  // 2026-09-28), so that is "Up to a set width" — show it as such.
  const size = config.placement.size;
  if (size.width === 'auto' && size.max_width_px !== null) size.width = 'max';
  // One fixed height, as the server reads it: max wins, then a lone
  // reservation, then the default (a stored "grow" is a fixed card now).
  const height = size.max_height_px ?? size.min_height_px ?? CARD_HEIGHT_DEFAULT;
  size.max_height_px = height;
  size.min_height_px = height;
  const mode = config.scope.default_mode as unknown;
  if (mode !== null && mode !== 'photo' && mode !== 'audio' && mode !== 'live' && mode !== 'chat') config.scope.default_mode = null;
  // A stored config from before chat mode (spec 014) has no `chat` key: the
  // merge fills it from the new-site default, on, matching the server
  // (withPartnerChatDefault: chat defaults on for partners).
  return {
    meta: {
      version: typeof record.version === 'number' ? record.version : 0,
      published: record.published === true,
      updatedAt: typeof record.updated_at === 'string' ? record.updated_at : null,
      updatedBy: typeof record.updated_by === 'number' ? record.updated_by : null,
    },
    config,
    extensions,
  };
}

/** Keeps only readable action items (the read path is forgiving, like the server's). */
export function normaliseActions(raw: unknown): RoutingAction[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): RoutingAction[] => {
    if (!isRecord(item) || typeof item.id !== 'string') return [];
    const mode = ACTION_MODES.find((m) => m === item.mode);
    if (!mode) return [];
    return [
      {
        id: item.id,
        mode,
        label: typeof item.label === 'string' ? item.label : '',
        phone: typeof item.phone === 'string' ? item.phone : null,
        url: typeof item.url === 'string' ? item.url : null,
        trades: Array.isArray(item.trades) ? item.trades.filter((t): t is string => typeof t === 'string') : [],
      },
    ];
  });
}

/** Groups whose value differs between two models. */
export function changedGroups(base: WidgetConfigModel, edited: WidgetConfigModel): ConfigGroup[] {
  return CONFIG_GROUPS.filter((group) => JSON.stringify(base[group]) !== JSON.stringify(edited[group]));
}

/** The `PUT /config/draft` body: whole objects for the changed groups only. */
export function draftPatch(
  edited: WidgetConfigModel,
  groups: readonly ConfigGroup[],
  extensions: LoadedConfig['extensions'] = {},
): Partial<Record<ConfigGroup, unknown>> {
  const body: Partial<Record<ConfigGroup, unknown>> = {};
  for (const group of groups) {
    body[group] = { ...clone(edited[group]), ...(extensions[group] ?? {}) };
  }
  return body;
}

/** Leaf paths (`branding.colors.primary`) whose values differ. */
export function diffPaths(a: unknown, b: unknown, prefix = ''): string[] {
  if (isRecord(a) && isRecord(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].flatMap((key) => diffPaths(a[key], b[key], prefix ? `${prefix}.${key}` : key));
  }
  return JSON.stringify(a) === JSON.stringify(b) ? [] : [prefix];
}

/** Immutable set of a dotted path — how section components update the model. */
export function setPath<T>(model: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const root = clone(model) as unknown as Record<string, unknown>;
  let cursor: Record<string, unknown> = root;
  keys.forEach((key, i) => {
    if (i === keys.length - 1) {
      cursor[key] = value;
    } else {
      cursor[key] = isRecord(cursor[key]) ? { ...(cursor[key] as Record<string, unknown>) } : {};
      cursor = cursor[key] as Record<string, unknown>;
    }
  });
  return root as unknown as T;
}

export function getPath(model: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => (isRecord(acc) ? acc[key] : undefined), model);
}

/**
 * decisions/fallback-network.md §1, mirrored from the server's
 * `fallbackNetworkFor` (schemas/widget-config.ts): warranty, insurer and
 * membership partners compete with eLocal's owner, so they get a neutral
 * network; every other type, and a site with no partner, gets eLocal.
 */
const NEUTRAL_FALLBACK_TYPES = new Set(['home_warranty', 'insurer', 'membership']);
export function fallbackNetworkFor(partnerType: string | null | undefined): 'elocal' | 'neutral' {
  return partnerType && NEUTRAL_FALLBACK_TYPES.has(partnerType) ? 'neutral' : 'elocal';
}

/**
 * The body the branding preview feeds the real widget as its
 * `GET /widget/config` answer: the draft plus the response-only fields
 * (contracts/widget-config.md §Response).
 */
export function toWidgetResponse(
  config: WidgetConfigModel,
  args: { siteKey: string; version: number; partner: { id: string; display_name: string; type: string } | null },
) {
  return {
    schema_version: '1.0',
    version: args.version,
    site_key: args.siteKey,
    environment: 'test' as const,
    partner: args.partner,
    ...clone(config),
    scope: {
      ...clone(config.scope),
      live: { camera: config.scope.live.camera, max_seconds: config.scope.modes.live ? 300 : 0 },
      chat: { max_seconds: config.scope.modes.chat ? 600 : 0 },
    },
    routing: { ...clone(config.routing), finderly_network_fallback: false, fallback_network: fallbackNetworkFor(args.partner?.type) },
  };
}
